import React, { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from '@fortawesome/pro-solid-svg-icons';
import { faArrowProgress, faChartSimple, faCubes, faDownload, faDrawPolygon, faRulerCombined, faTableLayout } from '@fortawesome/pro-light-svg-icons';
import { NavLink, useLocation } from 'react-router-dom';

import { useOptionalProjectContext } from "@/services/context/project/use-project-context.ts";

import { SideBarItem, SubSideBarItem } from './side-bar-item.ts';
import classNames from "@/utils/class-names.ts";
import FlowMode from "@/models/enums/flow-mode.ts";
import ClosableDialog from "@/components/dialogs/closable-dialog";
import useAuthenticationContext from '@/services/context/authentication/use-authentication-context.ts';

const parseUrl = function (url: string): { pathname: string; searchParams: URLSearchParams } {
    if (!url) return { pathname: '', searchParams: new URLSearchParams() };
    try {
        // Supporte les URLs relatives et absolues
        const u = new URL(url, 'http://local');
        return { pathname: u.pathname, searchParams: u.searchParams };
    } catch {
        const [rawPath = '', rawQuery = ''] = url.split('?');
        const pathname = rawPath.startsWith('/') ? rawPath : `/${rawPath}`;
        return { pathname, searchParams: new URLSearchParams(rawQuery) };
    }
}

const isActiveMatch = function (currentUrl: string, linkUrl: string): boolean {
    const current = parseUrl(currentUrl);
    const link = parseUrl(linkUrl);

    const linkPath = link.pathname;
    if (!linkPath) return false;

    const eq = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: 'accent' }) === 0;
    const starts = (a: string, b: string) => a.toLowerCase().startsWith(b.toLowerCase());

    // 1) Matching sur le pathname 
    let pathMatches = false;

    // Direct ou préfixe
    if (eq(current.pathname, linkPath) || starts(current.pathname, linkPath)) pathMatches = true;

    // Suffixe /Index
    const indexSuffix = '/index';
    if (!pathMatches && linkPath.toLowerCase().endsWith(indexSuffix)) {
        const withoutIndex = linkPath.slice(0, -indexSuffix.length) || '/';
        if (eq(current.pathname, withoutIndex) || starts(current.pathname, withoutIndex)) pathMatches = true;
    }

    // Slash final
    if (!pathMatches && linkPath.endsWith('/') && linkPath.length > 1) {
        const withoutSlash = linkPath.replace(/\/+$/, '');
        if (eq(current.pathname, withoutSlash) || starts(current.pathname, withoutSlash)) pathMatches = true;
    }

    if (!pathMatches) return false;

    // 2) Si le lien contient des query params: on ne compare QUE le premier (clé+valeur)
    const firstLinkEntry = Array.from(link.searchParams.entries())[0];
    if (!firstLinkEntry) return true;

    const [firstKey, firstValue] = firstLinkEntry;
    return current.searchParams.get(firstKey) === firstValue;
}

const Sidebar: React.FC = () => {
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    const location = useLocation();

    const projectContext = useOptionalProjectContext();

    const { hasLicencePermission } = useAuthenticationContext();

    // On vérifie si on est dans une route de type /takeoff/
    const takeoffMatch = useMemo(() => location.pathname.match(/\/takeoff\/?([^/]*)/), [location.pathname]);

    const currentSidebarItems = useMemo<SideBarItem[]>(() => {
        if (takeoffMatch && projectContext) {
            const projectId = takeoffMatch[1]; // Récupère l'ID dans le cas où l'utilisateur est sur /takeoff/:id
            const boomerUrl = import.meta.env.VITE_BOOMER_URL;
            const is2D3D = projectContext.project.flowMode === FlowMode._2D3D;
            let subChildren: SubSideBarItem[];
            if (is2D3D) {
                subChildren = [
                    { text: 'Surfaces', action: () => setIsDialogOpen(true) },
                    { text: 'Carbone', action: () => setIsDialogOpen(true) },
                    { text: 'Préparation DCE', action: () => setIsDialogOpen(true) }
                ];
            } else {
                subChildren = [
                    { text: 'Surfaces', path: `${boomerUrl}/Management/Surfaces/${projectContext.project.projectBoomerId}` },
                    { text: 'Carbone', path: `${boomerUrl}/Carbone/Surfaces/${projectContext.project.projectBoomerId}` },
                    { text: 'Préparation DCE', path: `${boomerUrl}/Purchase/LotsMasonry/${projectContext.project.projectBoomerId}` }
                ];
            }

            const items: SideBarItem[] = [
                { text: 'Saisies', path: `/takeoff/${projectId}?tab=measures`, icon: faDrawPolygon, isLast: false, isLocked: !hasLicencePermission("VIEW_SAISIE") },
                { text: 'Extraction', path: `/takeoff/${projectId}?tab=extraction`, icon: faDownload, isLast: false, isLocked: !hasLicencePermission("VIEW_EXTRACTION_DQE") },
                { icon: faRulerCombined, text: 'REVUES', isLast: true, children: subChildren },
            ];

            if (is2D3D) {
                items.push({ text: 'Process IA', action: () => setIsDialogOpen(true), icon: faArrowProgress, isLast: true });
            } else {
                items.push({ text: 'Process IA', path: `/takeoff/${projectId}?tab=processes`, icon: faArrowProgress, isLast: true, isLocked: !hasLicencePermission("VIEW_WORKFLOW") });
            }

            return items;
        }

        return [
            { text: 'Projets', path: '/projects', icon: faCubes, isLast: false },
            { text: 'Indicateurs multi-projets', path: '/multi-projects', icon: faChartSimple, isLast: false, isLocked: !hasLicencePermission("VIEW_MULTI-PROJECT") },
        ];
    }, [projectContext, takeoffMatch, hasLicencePermission]);

    // Séparation firsts/lasts comme dans Razor
    const { firsts, lasts } = useMemo(() => {
        const first = currentSidebarItems.filter(i => !i.isLast);
        const last = currentSidebarItems.filter(i => i.isLast);
        return { firsts: first, lasts: last };
    }, [currentSidebarItems]);

    const renderLeaf = (item: SideBarItem) => {
        if (item.action != null) {
            return (
                <button onClick={item.action} className='w-full cursor-pointer' key={item.text}>
                    <div className={'flex text-xs uppercase items-center p-2 rounded-sm relative hover:text-gray-800'}>
                        <FontAwesomeIcon icon={item.icon} fixedWidth className='shrink-0 text-lg me-2' />
                        <span className={classNames('tracking-widest select-none ', !sidebarOpen ? 'hidden' : '')}>
                            {item.text}
                        </span>
                        {item.isLocked &&
                            <div className='absolute right-0 p-3' title='Fonctionnalité non disponible dans votre licence'>
                                <FontAwesomeIcon icon={faLock} className='text-red-500' />
                            </div>
                        }
                    </div>
                </button>
            )
        }

        if (item.path != null) {
            const currentFullUrl = location.pathname + location.search;
            const isMatch = isActiveMatch(currentFullUrl, item.path ?? '');

            const isExternal = item.path.startsWith('http');
            const navLinkClass = () => {
                const customActive = !item.isLocked && item.path ? isMatch : false;

                let stateClass = '';
                if (!item.isLocked) {
                    stateClass = customActive ? 'text-gray-950 font-medium pointer-events-none' : 'hover:text-gray-800';
                }

                return classNames(stateClass, 'text-xs uppercase flex flex-row items-center p-2 w-full relative');
            };

            const content = (
                <>
                    <FontAwesomeIcon icon={item.icon} fixedWidth className='shrink-0 text-lg me-2' />
                    <span className={classNames('tracking-widest select-none text-wrap', !sidebarOpen ? 'hidden' : '')}>
                        {item.text}
                    </span>
                    {item.isLocked &&
                        <div className='absolute right-0 p-3' title='Fonctionnalité non disponible dans votre licence'>
                            <FontAwesomeIcon icon={faLock} className='text-red-500' />
                        </div>
                    }
                </>
            );

            if (isExternal) {
                return (
                    <a href={item.path} key={item.text} className={navLinkClass()}>
                        {content}
                    </a>
                )
            }

            if (item.isLocked) {
                return (
                    <div key={item.text} className={navLinkClass()}>
                        {content}
                    </div>
                );
            }

            return (
                <NavLink to={item.path} key={item.text} className={navLinkClass}>
                    {content}
                </NavLink>
            );
        }

        return <>{item.text}</>
    };

    const renderGroup = (item: SideBarItem) => {
        // groupe actif si au moins un enfant correspond
        const isGroupActive = (item.children ?? []).some(c => isActiveMatch(location.pathname, c.path ?? ''));

        return (
            <div key={item.text} className='flex flex-col gap-y-1'>
                <div className={classNames(
                    'flex text-xs uppercase items-center p-2 rounded-sm relative',
                    isGroupActive ? 'text-gray-950 font-medium' : ''
                )}>
                    <FontAwesomeIcon icon={item.icon} fixedWidth className='shrink-0 text-lg me-2' />
                    <span className={classNames('tracking-widest select-none', !sidebarOpen ? 'hidden' : '')}>
                        {item.text}
                    </span>
                    {item.isLocked &&
                        <div className='absolute right-0 p-3' title='Fonctionnalité non disponible dans votre licence'>
                            <FontAwesomeIcon icon={faLock} className='text-red-500' />
                        </div>
                    }
                </div>

                <ul className={classNames('border-l border-gray-300 flex flex-col gap-y-1 ml-9', !sidebarOpen ? 'hidden' : '')}>
                    {(item.children ?? []).map((sub, index) => {

                        const isLocked = !!item.isLocked;
                        const baseItemClass = 'text-sm flex -ml-px my-auto py-1 border-l';

                        if (sub.action != null) {
                            return (
                                <li key={index} className={classNames(baseItemClass, 'hover:border-gray-800 hover:text-gray-800')}>
                                    <button onClick={sub.action} className='w-full select-none pl-2.5 flex cursor-pointer'>
                                        {sub.text}
                                    </button>
                                </li>
                            )
                        }

                        if (sub.path != null) {
                            const active = isActiveMatch(location.pathname, sub.path);
                            const isExternal = sub.path.startsWith('http');

                            let stateClass = '';
                            if (!isLocked) {
                                stateClass = active
                                    ? 'text-gray-950 border-gray-950 font-semibold'
                                    : 'border-transparent hover:border-gray-800 hover:text-gray-800';
                            }

                            if (isExternal) {
                                return (
                                    <li key={index} className={classNames(baseItemClass, stateClass)}>
                                        {isLocked ? (
                                            <span className='pl-2.5 w-full select-none'>
                                                {sub.text}
                                            </span>
                                        ) : (
                                            <a href={sub.path} className='pl-2.5 w-full select-none'>
                                                {sub.text}
                                            </a>
                                        )}
                                    </li>
                                );
                            }

                            return (
                                <li key={index} className={classNames(baseItemClass, stateClass)}>
                                    {isLocked ? (
                                        <span className='pl-2.5 w-full select-none'>
                                            {sub.text}
                                        </span>
                                    ) : (
                                        <NavLink to={sub.path} className='pl-2.5 w-full select-none'>
                                            {sub.text}
                                        </NavLink>
                                    )}
                                </li>
                            );
                        }

                        return <>{sub.text}</>
                    })}
                </ul>
            </div>
        );
    };

    const renderItems = (items: SideBarItem[]) => (
        <>
            {items.map(item => {
                if (item.children && item.children.length > 0) {
                    return renderGroup(item);
                }
                return renderLeaf(item);
            })}
        </>
    );

    return (
        <>
            <ul id='sidebar'
                className={classNames('flex flex-col h-full border-r border-gray-100 transition-all duration-300 ease-in-out bg-white text-gray-400 px-3 py-2 overflow-y-auto', !sidebarOpen ? 'w-15' : 'w-48')}>
                <div className={classNames('flex flex-row items-center', !sidebarOpen ? 'mx-auto' : '')}>
                    <span className={classNames('flex-1 text-xl font-opensans select-none', !sidebarOpen ? 'hidden' : '')}>
                        {takeoffMatch ? 'Métrés' : 'Accueil'}
                    </span>

                    <button type='button'
                        className='inline-flex items-center justify-center w-8 h-8 rounded-sm hover:bg-gray-50 hover:text-gray-700 shrink-0'
                        onClick={() => setSidebarOpen(o => !o)}
                        title='Ouvrir/Fermer le panneau'>
                        <FontAwesomeIcon icon={faTableLayout} />
                    </button>
                </div>

                <li className='flex flex-col gap-y-1 flex-1 mt-3'>
                    {renderItems(firsts)}
                </li>

                <li className='flex flex-col gap-y-1 shrink-0'>
                    {lasts.length > 0 && renderItems(lasts)}

                    <a className={classNames('flex flex-row px-1 py-1 items-center', !sidebarOpen ? 'mx-auto' : '')}
                        href='https://www.treegram.fr' target='_blank' rel='noreferrer'>

                        <img className='size-6' src='/images/tgmlogos/logo.png' alt='Treegram logo' />
                        <span className={classNames('pl-[0.7rem] font-sans tracking-widest select-none', !sidebarOpen ? 'hidden' : '')}>
                            TREEGRAM
                        </span>
                    </a>
                </li>
            </ul>

            <ClosableDialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)} title='Non disponible dans un projet PDF simple'>
                <p>Créez un projet PDF superposé pour avoir accès aux Revues de projet et aux Process.</p>
            </ClosableDialog>
        </>
    );
};

export default Sidebar;